import React, { useState } from "react";
import RecordCard from "./RecordCard";
import RecordModal from "./RecordModal";

const normalizeSearchValue = (value) =>
    (value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const normalizeSortValue = (value) =>
    (value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\(\d+\)/g, "")
        .replace(/^(the|a|an)\s+/i, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

function RecordGrid({ records, discogsData, onNowPlaying }) {
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [trackList, setTrackList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOption, setSortOption] = useState("artist");

    const resolveCoverUrl = (cover) => {
        if (!cover) return "https://rjbogz.com/records/default.png";
        if (cover.startsWith("http://") || cover.startsWith("https://")) return cover;
        return `https://rjbogz.com/records/${cover}`;
    };

    const handleCardClick = (id) => {
        const record = records[id];
        const discogsInfo = discogsData[String(record.discogs_id)] || {};

        const combinedRecord = {
            ...record,
            ...discogsInfo,
            cover: discogsInfo.cover || resolveCoverUrl(record.cover),
        };

        setSelectedRecord(combinedRecord);
        setTrackList(discogsInfo.trackList || []);
    };

    const normalizedSearch = normalizeSearchValue(searchTerm);

    const filteredRecords = Object.entries(records).filter(([, record]) => {
        if (!normalizedSearch) return true;

        const discogsInfo = discogsData[String(record.discogs_id)] || {};
        const trackNames = [
            ...(discogsInfo.trackNames || []),
            ...((discogsInfo.trackList || []).map((track) => track.title)),
            ...((record.trackNames || [])),
            ...((record.tracks || []).map((track) =>
                typeof track === "string" ? track : track.title
            )),
        ].filter(Boolean);

        const searchableValues = [
            record.artist,
            record.album,
            record.title,
            ...trackNames,
        ];

        return searchableValues.some((value) =>
            normalizeSearchValue(value).includes(normalizedSearch)
        );
    });

    const sortedRecords = filteredRecords.sort(([, a], [, b]) => {
        if (sortOption === "artist") {
            const artistCompare = normalizeSortValue(a.artist).localeCompare(
                normalizeSortValue(b.artist),
                undefined,
                {
                    sensitivity: "base",
                    numeric: true,
                }
            );

            if (artistCompare !== 0) return artistCompare;

            const albumCompare = normalizeSortValue(a.album).localeCompare(
                normalizeSortValue(b.album),
                undefined,
                {
                    sensitivity: "base",
                    numeric: true,
                }
            );

            if (albumCompare !== 0) return albumCompare;

            return (a.album || "").localeCompare(b.album || "", undefined, {
                sensitivity: "base",
                numeric: true,
            });
        } else if (sortOption === "album") {
            const albumCompare = normalizeSortValue(a.album).localeCompare(
                normalizeSortValue(b.album),
                undefined,
                {
                    sensitivity: "base",
                    numeric: true,
                }
            );

            if (albumCompare !== 0) return albumCompare;

            return normalizeSortValue(a.artist).localeCompare(
                normalizeSortValue(b.artist),
                undefined,
                {
                    sensitivity: "base",
                    numeric: true,
                }
            );
        }

        return 0;
    });

    return (
        <div className="record-grid">
            <div className="controls">
                <input
                    type="text"
                    placeholder="Search artist, album, or track..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                >
                    <option value="artist">Sort by Artist</option>
                    <option value="album">Sort by Album</option>
                </select>
            </div>

            <div className="grid">
                {sortedRecords.map(([id, record]) => (
                    <RecordCard
                        key={id}
                        record={record}
                        onClick={() => handleCardClick(id)}
                    />
                ))}
            </div>

            {selectedRecord && (
                <RecordModal
                    record={selectedRecord}
                    trackList={trackList}
                    onClose={() => {
                        setSelectedRecord(null);
                        setTrackList([]);
                    }}
                />
            )}
        </div>
    );
}

export default RecordGrid;
